/**
 * @desc       Fake Asset Generator (I think??)
 * @version    1.1 (Converted moment to date-fns) - 23 May 2024
 * */

import { addDays, format, subMinutes, subMonths } from 'date-fns'
import { computeOffset } from 'helpers/maps.js'
import { randomIntFromInterval } from 'helpers/maths.js'
import { verticalCylinderVolume } from 'helpers/tanks.js'

let fakeTanks = []
let fakeTroughs = []
let fakeRainBuckets = []
let fakeVehicles = []
let fakeFences = []
let latitude = -32
let longitude = 149
let farmId = 0

export function generateAssets($farm) {
    latitude = $farm.latitude
    longitude = $farm.longitude
    farmId = $farm.id
    let tempAssets = []
    if (fakeTanks.length) {
        return [...fakeTanks, ...fakeTroughs, ...fakeRainBuckets, ...fakeVehicles, ...fakeFences]
    } else {
        tempAssets.push(generateTank(1))
        tempAssets.push(generateTank(2))
        tempAssets.push(generateTank(3))
        tempAssets.push(generateTrough(4))
        tempAssets.push(generateTrough(5))
        tempAssets.push(generateTrough(6))
        tempAssets.push(generateRainGauge(7))
        tempAssets.push(generateRainGauge(8))
        tempAssets.push(generateRainGauge(9))
        tempAssets.push(generateVehicle(10))
        tempAssets.push(generateVehicle(11, 'Ute'))
        tempAssets.push(generateVehicle(12, 'Tractor'))
    }
    return tempAssets
}

function randomSignalLevel() {
    return ['l', 'm', 'h'][randomIntFromInterval(0, 2)]
}

function generateVehicle($num = 1, $type = 'Quadbike') {
    const newLocation = computeOffset({ latitude: latitude, longitude: longitude }, randomIntFromInterval(100, 1000), randomIntFromInterval(0, 360))
    const newVehicle = {
        id: `${farmId}-${$num}`,
        farmId: farmId,
        detail: {
            description: `An example description for the ${$type}`,
            regoNumber: `FRMDCK${$num}`,
            meters: { kilometres: { value: randomIntFromInterval(5000, 150000) }, engineHours: { value: randomIntFromInterval(23, 1000) } },
            maint: {
                lastService: { at: format(subMonths(new Date(), randomIntFromInterval(30, 364)), 'yyyy-MM-dd') },
                nextService: { at: format(addDays(new Date(), randomIntFromInterval(30, 364)), 'yyyy-MM-dd') },
            },
            modelNumber: 'M' + randomIntFromInterval(5000, 150000),
            serialNumber: randomIntFromInterval(123456, 1234567),
            purchaseDate: subMonths(new Date(), randomIntFromInterval(30, 364)),
            nextRegoRenewal: addDays(new Date(), randomIntFromInterval(30, 364)),
            nextInsuranceRenewal: addDays(new Date(), randomIntFromInterval(30, 364)),
            insuranceProvider: `GIO-FARMDECK-${$num}`,
            isFake: true,
        },
        name: `Example ${$type} ${$num}`,
        sensorIds: { [`${$num}|${$type}`]: ['latitude', 'longitude'] },
        type: `vehicle${$type}`,
        payloads: {
            [`${$num}|${$type}`]: {
                sensorId: [`${$num}|${$type}`],
                timestamp: subMinutes(new Date(), randomIntFromInterval(10, 100)),
                content: {
                    battery: randomIntFromInterval(5, 100),
                    latitude: newLocation.latitude,
                    longitude: newLocation.longitude,
                    signal: randomSignalLevel(),
                },
            },
        },
        sensors: {
            [`${$num}|${$type}`]: {
                id: [$num],
                name: `Example ${$type} ${$num}`,
                type: 7,
                configuration: {
                    period: 60,
                },
            },
        },
        status: 'active',
        group: 'vehicle',
    }
    fakeVehicles.push(newVehicle)
    return newVehicle
}

function generateTrough($num = 1, $type = 'Trough') {
    const percent = randomIntFromInterval(10, 85, 100)
    const height = randomIntFromInterval(150, 300)
    const newLocation = computeOffset({ latitude: latitude, longitude: longitude }, randomIntFromInterval(100, 1000), randomIntFromInterval(0, 360))
    const newTrough = {
        id: `${farmId}-${$num}`,
        farmId: farmId,
        detail: {
            latitude: newLocation.latitude,
            longitude: newLocation.longitude,
            isFake: true,
            maxLevel: height,
        },
        name: `Example ${$type} ${$num}`,
        sensorIds: { [`${$num}|${$type}`]: ['level'] },
        type: 'waterTrough',
        payloads: {
            [`${$num}|${$type}`]: {
                sensorId: [`${$num}|${$type}`],
                timestamp: subMinutes(new Date(), randomIntFromInterval(10, 700)),
                content: {
                    battery: randomIntFromInterval(5, 100),
                    level: percent * height,
                    signal: randomSignalLevel(),
                },
            },
        },
        sensors: {
            [`${$num}|${$type}`]: {
                id: [`${$num}|${$type}`],
                name: `Example ${$type} ${$num}`,
                type: 7,
                configuration: {
                    period: 60,
                    fullHeight: height,
                },
            },
        },
        status: 'active',
        group: 'water',
    }
    fakeTroughs.push(newTrough)
    return newTrough
}

function generateRainGauge($num = 1, $type = 'RainGauge') {
    const newLocation = computeOffset({ latitude: latitude, longitude: longitude }, randomIntFromInterval(100, 1000), randomIntFromInterval(0, 360))
    const newRainBucket = {
        id: `${farmId}-${$num}`,
        farmId: farmId,
        detail: {
            latitude: newLocation.latitude,
            longitude: newLocation.longitude,
            isFake: true,
        },
        name: `Example ${$type} ${$num}`,
        sensorIds: { [`${$num}|${$type}`]: ['rainfall'] },
        type: 'rainBucket',
        payloads: {
            [`${$num}|${$type}`]: {
                sensorId: [`${$num}|${$type}`],
                timestamp: subMinutes(new Date(), randomIntFromInterval(10, 500)),
                content: {
                    battery: randomIntFromInterval(5, 100),
                    signal: randomSignalLevel(),
                    rainfall: 109.8,
                    rainfallDay: randomIntFromInterval(1, 35, 10),
                    rainfallMonth: randomIntFromInterval(100, 400, 10),
                    rainfallRollingYear: randomIntFromInterval(100, 400),
                },
            },
        },
        sensors: {
            [`${$num}|${$type}`]: {
                id: [$num],
                name: `Example ${$type} ${$num}`,
                type: 2,
                configuration: {
                    period: 60,
                },
            },
        },
        status: 'active',
        group: 'water',
    }
    fakeRainBuckets.push(newRainBucket)
    return newRainBucket
}

function generateTank($num = 1, $type = 'Tank') {
    const percent = randomIntFromInterval(10, 85, 100)
    const diameter = randomIntFromInterval(20, 100, 10)
    const height = randomIntFromInterval(10, 100, 10)
    const volume = verticalCylinderVolume(diameter, height)
    const newLocation = computeOffset({ latitude: latitude, longitude: longitude }, randomIntFromInterval(100, 1000), randomIntFromInterval(0, 360))
    const newTank = {
        id: `${farmId}-${$num}`,
        farmId: farmId,
        detail: {
            latitude: newLocation.latitude,
            longitude: newLocation.longitude,
            tankCapacity: parseInt(volume),
            tankConstructType: 'Flat walled',
            tankDiameter: diameter,
            tankElevation: 'Ground level',
            tankHeight: height,
            tankManufacturer: 'Other',
            tankMaterial: 'Polyethylene',
            tankModel: 'Unknown',
            tankShape: 'Vertical Cylinder Tank',
            usage: 'Drinking',
            isFake: true,
            maxLevel: height * 1000,
        },
        maintainable: false,
        name: `Example ${$type} ${$num}`,
        description: '',
        sensorIds: { [`${$num}|${$type}`]: ['level'] },
        type: 'waterTank',
        payloads: {
            [`${$num}|${$type}`]: {
                sensorId: [`${$num}|${$type}`],
                timestamp: subMinutes(new Date(), randomIntFromInterval(10, 500)),
                content: {
                    battery: randomIntFromInterval(5, 100),
                    level: percent * height * 1000,
                    signal: randomSignalLevel(),
                },
            },
        },
        sensors: {
            [`${$num}|${$type}`]: {
                id: [$num],
                name: `Example ${$type} ${$num}`,
                type: 7,
                configuration: {
                    period: 60,
                    fullHeight: height * 1000,
                    range: 5000,
                },
            },
        },
        status: 'active',
        group: 'water',
    }
    fakeTanks.push(newTank)
    return newTank
}
